import { type Cell, flexRender, type Row } from '@tanstack/react-table'
import { cn } from '../../lib/utils'
import { DataTableActions } from './data-table-actions'
import {
  type DataTablePhoneRole,
  PHONE_TABLE_CLASSES,
  phoneCellClass,
} from './data-table-phone-layout'
import { TableCell } from './table'

export function DataTablePhoneDetailLine<TData>({
  cells,
  detailIndexes,
  row,
  colSpan,
  hasSelection,
  hasActions,
}: {
  cells: Cell<TData, unknown>[]
  detailIndexes: Map<string, number>
  row: Row<TData>
  colSpan: number
  hasSelection: boolean
  hasActions: boolean
}) {
  const details = cells.flatMap((cell) => {
    if (!detailIndexes.has(cell.column.id)) return []
    const phoneValue = cell.column.columnDef.meta?.phoneValue
    if (phoneValue) {
      const value = phoneValue(row)
      return value === null || value === undefined || value === '' ? [] : [{ cell, value }]
    }
    if (cell.column.accessorFn) {
      const value = cell.getValue()
      if (value === null || value === undefined || value === '') return []
    }
    return [{ cell, value: flexRender(cell.column.columnDef.cell, cell.getContext()) }]
  })
  if (details.length === 0) return null

  return (
    <TableCell
      colSpan={colSpan}
      data-phone-detail-line
      className={cn(
        PHONE_TABLE_CLASSES.detailLine,
        hasSelection && PHONE_TABLE_CLASSES.detailLineAfterSelection,
        hasSelection &&
          (hasActions
            ? PHONE_TABLE_CLASSES.detailLineAfterSelectionWithActions
            : PHONE_TABLE_CLASSES.detailLineAfterSelectionWithoutActions),
        !hasSelection && !hasActions && PHONE_TABLE_CLASSES.detailLineWithoutSelectionOrActions,
      )}
    >
      <div className="overflow-hidden">
        <div data-phone-detail-list className="-ms-4 flex flex-wrap items-baseline gap-y-1">
          {details.map(({ cell, value }) => {
            const label =
              cell.column.columnDef.meta?.phoneLabel ??
              cell.column.columnDef.meta?.label ??
              (typeof cell.column.columnDef.header === 'string'
                ? cell.column.columnDef.header
                : cell.column.id)
            const phoneLabel = cell.column.columnDef.meta?.phoneLabel

            return (
              // A flex row, so the line never breaks between a separator and its value
              <div key={cell.id} className="flex min-w-0 items-baseline">
                <span
                  aria-hidden="true"
                  data-phone-detail-separator
                  className="inline-block w-4 shrink-0 text-center"
                >
                  ·
                </span>
                <bdi data-phone-detail-value className="min-w-0 @max-[40rem]:inline">
                  {phoneLabel ? (
                    <span aria-hidden="true" data-phone-detail-label>
                      {phoneLabel}:{' '}
                    </span>
                  ) : null}
                  {/* Its own dir keeps it out of the bdi's auto direction (HTML spec): the
                      value alone decides, so an English label cannot turn an Arabic
                      name left-to-right and truncate its start. */}
                  <span className="sr-only" dir="auto">
                    {label}:{' '}
                  </span>
                  {value}
                </bdi>
              </div>
            )
          })}
        </div>
      </div>
    </TableCell>
  )
}

export function DataTablePhoneRowDetails<TData>({
  cells,
  row,
  roles,
  detailIndexes,
  hasSelection,
  hasActions,
}: {
  cells: Cell<TData, unknown>[]
  row: Row<TData>
  roles: Map<string, DataTablePhoneRole>
  detailIndexes: Map<string, number>
  hasSelection: boolean
  hasActions: boolean
}) {
  return (
    <>
      <DataTablePhoneDetailLine
        cells={cells}
        detailIndexes={detailIndexes}
        row={row}
        colSpan={cells.length}
        hasSelection={hasSelection}
        hasActions={hasActions}
      />
      {cells
        .filter((cell) => roles.get(cell.column.id) === 'actions')
        .map((cell) => (
          <TableCell
            key={cell.id}
            data-phone-role="actions"
            className={phoneCellClass('actions', {
              selection: false,
              actions: false,
              hasSelection,
            })}
            onClick={(event) => event.stopPropagation()}
          >
            {cell.column.columnDef.meta?.actions ? (
              <DataTableActions
                actions={cell.column.columnDef.meta.actions(row)}
                label={cell.column.columnDef.meta.actionsLabel?.(row)}
                phoneRows
              />
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        ))}
    </>
  )
}
